import { Text } from "@overmap-ai/blocks"
import { type IssueAssociation, type Stored, UNLABELLED_ASSET_LABEL, UNTITLED_ISSUE_TITLE } from "@overmap-ai/common"
import { selectAssetById, selectIssueById, selectUserById } from "@overmap-ai/core"
import { Link } from "@tanstack/react-router"
import { memo } from "react"

import { interactiveTextCva } from "../../cva"
import { ProjectTimelineItemType } from "../../enums/projects"
import { useAppSelector } from "../../store"
import { formatCompactDate } from "../../utils/date"
import { getUserDisplayLabel } from "../../utils/users"
import { AssetTypeIcon } from "../Icons/AssetTypeIcon"
import { IssueTypeIcon } from "../Icons/IssueTypeIcon"
import { AssetIndexLabel } from "../IndexLabels/AssetIndexLabel"
import { IssueIndexLabel } from "../IndexLabels/IssueIndex"
import { ProjectTimelineCard } from "./ProjectTimelineCard"

interface ProjectTimelineIssueAssociationItemProps {
	issueAssociation: Stored<IssueAssociation>
}

export const ProjectTimelineIssueAssociationItem = memo<ProjectTimelineIssueAssociationItemProps>((props) => {
	const { issueAssociation } = props

	const user = useAppSelector(selectUserById(issueAssociation.created_by ?? -1))
	const issue = useAppSelector(selectIssueById(issueAssociation.issue))!
	const asset = useAppSelector(selectAssetById(issueAssociation.asset))!

	return (
		<ProjectTimelineCard type={ProjectTimelineItemType.ISSUE_ASSOCIATION}>
			<Text size="sm" accentColor="base" className="min-w-0">
				<Text highContrast>{getUserDisplayLabel(user)}</Text>
				&nbsp;associated issue&nbsp;
				<Link
					from="/$organizationId/$projectId"
					to="/$organizationId/$projectId/issues/$issueId"
					params={{ issueId: issue.uuid }}
				>
					<Text highContrast className={interactiveTextCva()}>
						<IssueTypeIcon className="align-sub" issueTypeId={issue.issue_type} />
						&nbsp;
						<IssueIndexLabel issueId={issue.uuid} highContrast />
						<Text accentColor="base">&nbsp;{issue.title || UNTITLED_ISSUE_TITLE}</Text>
					</Text>
				</Link>
				<Text accentColor="base">&nbsp;&#8226;&nbsp;{formatCompactDate(issueAssociation.submitted_at)}</Text>
			</Text>

			<div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
				<Text size="sm" accentColor="base" className="min-w-0">
					Context:&nbsp;
					<Link
						from="/$organizationId/$projectId"
						to="/$organizationId/$projectId/assets/$assetId"
						params={{ assetId: asset.uuid }}
					>
						<AssetTypeIcon className="align-sub" assetTypeId={asset.asset_type} />
						&nbsp;
						<Text className={interactiveTextCva()} accentColor="base">
							<AssetIndexLabel assetId={asset.uuid} highContrast />
							&nbsp;
							<Text>{asset.label || UNLABELLED_ASSET_LABEL}</Text>
						</Text>
					</Link>
				</Text>
			</div>
		</ProjectTimelineCard>
	)
})
ProjectTimelineIssueAssociationItem.displayName = "ProjectTimelineIssueAssociationItem"
